export type QualificationScope = "standard" | "annex_j" | "annex_k" | "annex_m";
export type ThicknessRange = {
  minMm: number | null;
  maxMm: number | null;
  qualifiedGauges?: number[];
  description: string;
};

export type AnnexJQualification = {
  code: string;
  name: string;
  qualifiedJoint: string;
  rule: "star" | "tested_to_3" | "tested_only" | "spot_gauge";
};

// CSA W47.1 Annex J, Table J.4 qualification names supplied from the standard screenshots.
export const ANNEX_J_QUALIFICATIONS: AnnexJQualification[] = [
  { code: "T-G1", name: "Square groove weld in butt joint welded from one side — sheet to sheet", qualifiedJoint: "Square groove weld in butt joint — sheet to sheet", rule: "star" },
  { code: "T-G2", name: "Square groove weld in butt joint welded from one side — sheet to tube", qualifiedJoint: "Square groove weld in butt joint — sheet to tube", rule: "star" },
  { code: "S-G1", name: "Square groove weld in butt joint welded from both sides — sheet to sheet", qualifiedJoint: "Square groove weld in butt joint — sheet to sheet", rule: "star" },
  { code: "FW-G1", name: "Fillet weld in a lap joint — sheet to sheet and sheet to supporting steel", qualifiedJoint: "Fillet weld in a lap or corner joint — sheet to sheet and sheet to supporting steel", rule: "tested_to_3" },
  { code: "FW-G2", name: "Fillet weld in a lap joint — sheet to supporting steel", qualifiedJoint: "Fillet weld in a lap or corner joint — sheet to supporting steel", rule: "tested_to_3" },
  { code: "FW-G3", name: "Fillet weld in a T-joint — sheet to sheet steel and sheet to supporting steel", qualifiedJoint: "Fillet weld in a T-, lap, or corner joint — sheet to sheet steel and sheet to supporting steel", rule: "tested_to_3" },
  { code: "FW-G4", name: "Fillet weld in a T-joint — sheet to supporting steel", qualifiedJoint: "Fillet weld in a T-, lap, or corner joint — sheet to supporting steel", rule: "tested_to_3" },
  { code: "FW-G5", name: "Fillet weld in a T-joint — tube to tube and tube to supporting steel", qualifiedJoint: "Fillet weld in a T-joint — tube to tube and tube to supporting steel", rule: "tested_to_3" },
  { code: "FB-G1", name: "Flare bevel groove weld — sheet to sheet and sheet to supporting steel", qualifiedJoint: "Flare bevel groove weld — sheet to sheet and sheet to supporting steel, and flare vee groove weld — sheet to sheet", rule: "tested_to_3" },
  { code: "FB-G2", name: "Flare bevel groove weld — sheet to supporting steel", qualifiedJoint: "Flare bevel groove weld — sheet to supporting steel", rule: "tested_to_3" },
  { code: "FV-G1", name: "Flare vee groove weld — sheet to sheet and sheet to supporting steel", qualifiedJoint: "Flare vee groove weld — sheet to sheet and sheet to supporting steel", rule: "tested_to_3" },
  { code: "FV-G2", name: "Flare vee groove weld — sheet to supporting steel", qualifiedJoint: "Flare vee groove weld — sheet to supporting steel", rule: "tested_to_3" },
  { code: "AS-G", name: "Arc seam weld — sheet to steel", qualifiedJoint: "Arc seam weld — sheet to steel", rule: "tested_only" },
  { code: "AP-G", name: "Arc plug weld — sheet to sheet and sheet to supporting steel", qualifiedJoint: "Arc plug weld — sheet to sheet and sheet to supporting steel", rule: "tested_only" },
  { code: "ASW-G1", name: "Arc spot weld — sheet to sheet and sheet to supporting steel", qualifiedJoint: "Arc spot weld — sheet to sheet and sheet to supporting steel", rule: "spot_gauge" },
  { code: "ASW-G2", name: "Arc spot weld — sheet to supporting steel", qualifiedJoint: "Arc spot weld — sheet to supporting steel", rule: "spot_gauge" },
];

// Table J.4 footnote gauge matrix. Key = gauge used for qualification test.
export const ANNEX_J_SPOT_GAUGE_COVERAGE: Record<number, number[]> = {
  16: [16, 18],
  18: [16, 18, 20],
  20: [18, 20, 22],
  22: [20, 22, 24],
  24: [22, 24, 26],
};

export function calculateQualificationThickness(args: {
  materialDivision: "steel" | "aluminum";
  scope: QualificationScope;
  qualificationCategory?: string | null;
  annexJCode?: string | null;
  testThicknessMm?: number | null;
  testGauge?: number | null;
}): ThicknessRange | null {
  const { materialDivision, scope, qualificationCategory, annexJCode, testThicknessMm, testGauge } = args;

  // W47.2 aluminum qualification range supplied by user: 1.5 mm and above.
  if (materialDivision === "aluminum") {
    return { minMm: 1.5, maxMm: null, description: "1.5 mm and above" };
  }

  // Normal W47.1 and Annex K: 3 mm and above.
  if (scope === "standard" || scope === "annex_k") {
    return { minMm: 3, maxMm: null, description: "3 mm and above" };
  }

  if (scope === "annex_m") {
    if (qualificationCategory === "FW") {
      if (testThicknessMm != null && testThicknessMm >= 6 && testThicknessMm <= 10) {
        return { minMm: 3, maxMm: null, description: "FW-M: 3 mm and above" };
      }
      return null;
    }

    // S-M groove with backing and T-M open-root plate/pipe.
    if (["S", "T_PLATE", "T_PIPE"].includes(qualificationCategory || "")) {
      if (testThicknessMm === 6) return { minMm: 3, maxMm: 12, description: "3 mm to 12 mm" };
      if (testThicknessMm === 10) return { minMm: 3, maxMm: 20, description: "3 mm to 20 mm" };
      if ([13, 16, 19].includes(testThicknessMm || -1)) return { minMm: 3, maxMm: null, description: "3 mm and above" };
      return null;
    }
    return null;
  }

  if (scope === "annex_j") {
    const item = ANNEX_J_QUALIFICATIONS.find(x => x.code === annexJCode);
    if (!item) return null;

    if (item.rule === "spot_gauge") {
      if (testGauge == null) return null;
      const gauges = ANNEX_J_SPOT_GAUGE_COVERAGE[testGauge];
      return gauges ? { minMm: null, maxMm: null, qualifiedGauges: gauges, description: `Qualified gauges: ${gauges.join(", ")} Ga` } : null;
    }

    if (testThicknessMm == null && testGauge == null) return null;

    if (item.rule === "tested_only") {
      if (testThicknessMm == null) return null;
      return { minMm: testThicknessMm, maxMm: testThicknessMm, description: `Thickness tested only: ${testThicknessMm} mm` };
    }

    if (item.rule === "tested_to_3") {
      if (testThicknessMm == null) return null;
      return { minMm: testThicknessMm, maxMm: 3, description: `${testThicknessMm} mm to maximum 3 mm` };
    }

    // Table J.4 starred rule for T-G1, T-G2 and S-G1.
    // 10 Ga qualifies from 16 Ga up to 3 mm. 18 Ga qualifies 16 Ga and thinner.
    if (testGauge === 10) return { minMm: null, maxMm: 3, qualifiedGauges: [16], description: "10 Ga test: 16 Ga to 3 mm" };
    if (testGauge === 18) return { minMm: null, maxMm: null, qualifiedGauges: [16, 18, 20, 22, 24, 26], description: "18 Ga test: 16 Ga and thinner" };

    if (testThicknessMm == null) return null;
    return {
      minMm: testThicknessMm / 2,
      maxMm: Math.min(testThicknessMm * 2, 3),
      description: `1/2T to 2T, maximum 3 mm (${testThicknessMm / 2} mm to ${Math.min(testThicknessMm * 2, 3)} mm)`,
    };
  }

  return null;
}

export function annexMQualificationName(category: string | null | undefined) {
  if (category === "FW") return "FW-M";
  if (category === "S") return "S-M";
  if (category === "T_PLATE" || category === "T_PIPE") return "T-M";
  return "";
}
